import { useTheme } from '../../context/ThemeContext.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import StarRating from '../StarRating.jsx';

export default function FontGrid({
  fonts, fontsLoading, fontsError, pagination, page, setPage,
  previewText, previewSize, setPreviewSize, viewMode,
  selectedFontId, setSelectedFontId, selectedFontIds, toggleSelectFont, selectAllVisible,
  compareMode, comparisonFontIds, toggleCompareFont,
  toggleFavorite, toggleActivation, handleDeleteFont, setActiveCount,
  refetchFonts, hasSelection, searchQuery,
}) {
  const { theme } = useTheme();
  const { user } = useAuth();

  return (
    <div style={{ padding: '20px 24px 24px', flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBottom: hasSelection ? 80 : 24, paddingRight: selectedFontId ? 384 : 24 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        paddingBottom: 10, borderBottom: `2px solid ${theme.text}`, marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            type="checkbox"
            checked={fonts.length > 0 && selectedFontIds.size === fonts.length}
            onChange={selectAllVisible}
            style={{ accentColor: theme.accent, cursor: 'pointer' }}
          />
          <span style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: theme.textFaint }}>
            {hasSelection
              ? `${selectedFontIds.size} selected`
              : fontsLoading
                ? 'loading...'
                : `${pagination.total || 0} font ${(pagination.total || 0) !== 1 ? 'families' : 'family'}`
            }
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px 12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: theme.textFaint }}>preview size</span>
          <input
            type="range" min={16} max={72} value={previewSize}
            onChange={e => setPreviewSize(Number(e.target.value))}
            style={{ width: 90, accentColor: theme.accent }}
          />
        </div>
      </div>

      {fontsError && (
        <div style={{
          padding: '16px 20px', background: theme.redBg,
          border: `1px solid ${theme.redBorder}`, borderRadius: 0,
          color: theme.red, fontSize: 12, marginBottom: 16,
        }}>
          {fontsError}
          <button onClick={() => refetchFonts()} style={{
            marginLeft: 12, background: 'none', border: 'none',
            color: theme.red, textDecoration: 'underline', cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 12,
          }}>Retry</button>
        </div>
      )}

      <div style={{
        display: viewMode === 'grid' ? 'grid' : 'flex',
        flexDirection: viewMode === 'list' ? 'column' : undefined,
        gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))' : undefined,
        gap: viewMode === 'grid' ? 16 : 0,
      }}>
        {fonts.map(font => {
          const isSelected = selectedFontIds.has(font.id);
          const isComparing = comparisonFontIds.includes(font.id);
          const isHighlighted = isComparing || isSelected || selectedFontId === font.id;
          return (
          <div
            key={font.id}
            data-font-id={String(font.id)}
            onClick={() => {
              if (compareMode) { toggleCompareFont(font.id); return; }
              setSelectedFontId(selectedFontId === font.id ? null : font.id);
            }}
            style={{
              background: isHighlighted ? theme.accentBg : 'transparent',
              border: `1px solid ${isHighlighted ? theme.accentBorder : theme.border}`,
              borderRadius: 0,
              padding: viewMode === 'grid' ? '16px 18px' : '12px 20px',
              cursor: 'pointer', transition: 'border-color 0.15s ease',
              display: 'flex',
              flexDirection: viewMode === 'grid' ? 'column' : 'row',
              alignItems: viewMode === 'list' ? 'center' : undefined,
              gap: viewMode === 'list' ? 20 : 0,
              position: 'relative',
              borderBottom: viewMode === 'list' ? `1px solid ${theme.borderLight}` : undefined,
            }}
            onMouseEnter={e => {
              if (!isHighlighted) e.currentTarget.style.borderColor = theme.borderMedium;
              const cb = e.currentTarget.querySelector<HTMLElement>('.font-cb');
              if (cb) cb.style.opacity = '1';
            }}
            onMouseLeave={e => {
              if (!isHighlighted) e.currentTarget.style.borderColor = theme.border;
              const cb = e.currentTarget.querySelector<HTMLElement>('.font-cb');
              if (cb && !hasSelection) cb.style.opacity = '0';
            }}
          >
            {!compareMode && (
              <div className="font-cb" style={{
                position: 'absolute', top: 8, left: 8,
                opacity: hasSelection || isSelected ? 1 : 0,
                transition: 'opacity 0.15s',
              }}>
                <input
                  type="checkbox" checked={isSelected}
                  onClick={e => e.stopPropagation()}
                  onChange={() => toggleSelectFont(font.id)}
                  style={{ accentColor: theme.accent, cursor: 'pointer' }}
                />
              </div>
            )}

            {compareMode && (
              <div style={{ position: 'absolute', top: 8, left: 8 }}>
                <input type="checkbox" checked={isComparing} readOnly style={{ accentColor: theme.accent, cursor: 'pointer' }} />
              </div>
            )}

            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              marginBottom: viewMode === 'grid' ? 12 : 0,
              width: viewMode === 'list' ? 180 : undefined, flexShrink: 0,
              gap: 12,
            }}>
              <div>
                <div style={{ fontFamily: "'Aeonik', Arial, Helvetica, sans-serif", fontWeight: 450, fontSize: 15, color: theme.text, letterSpacing: '0.01em' }}>{font.name}</div>
                <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: theme.textFaint, marginTop: 4 }}>
                  {font.category} · {font.style_count} styles · {font.size_display}
                  {font.is_variable ? ' · variable' : ''}
                </div>
              </div>
              <button
                onClick={e => { e.stopPropagation(); toggleFavorite(font.id); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: font.is_favorited ? theme.accent : theme.textDisabled,
                  fontSize: 13, padding: 2, lineHeight: 1,
                }}
              >{font.is_favorited ? '★' : '☆'}</button>
            </div>

            <div style={{
              fontFamily: `'${font.family}', serif`,
              fontSize: previewSize, lineHeight: 1.25, color: theme.textSecondary,
              marginBottom: viewMode === 'grid' ? 14 : 0,
              overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: viewMode === 'list' ? 'nowrap' : undefined,
              maxHeight: viewMode === 'grid' ? Math.round(previewSize * 2.6) : undefined,
              flex: viewMode === 'list' ? 1 : undefined,
              minWidth: viewMode === 'list' ? 0 : undefined,
            }}>{previewText}</div>

            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
              borderTop: viewMode === 'grid' ? `1px solid ${theme.borderLight}` : undefined,
              paddingTop: viewMode === 'grid' ? 10 : undefined,
              width: viewMode === 'list' ? 260 : undefined, flexShrink: 0,
            }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {font.avg_rating > 0 && (
                  <StarRating avgRating={font.avg_rating} ratingCount={font.rating_count} size={10} />
                )}
                {(font.collections || []).map(c => (
                  <span key={c.name} style={{
                    fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.textFaint,
                  }}>{c.name}</span>
                ))}
                {(font.tags || []).map(t => (
                  <span key={t.id} style={{
                    fontSize: 10, letterSpacing: '0.06em',
                    padding: '2px 6px', border: `1px solid ${theme.border}`, color: theme.text,
                  }}>{t.name}</span>
                ))}
                {!!font.is_personal && (
                  <span style={{
                    fontSize: 9, fontWeight: 600, color: theme.textGhost,
                    border: `1px solid ${theme.borderLight}`,
                    padding: '1px 6px', letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                  }}>personal</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                {!!font.is_personal && font.uploaded_by === user?.id && (
                  <span
                    onClick={e => {
                      e.stopPropagation();
                      if (confirm(`Delete "${font.name}"? This cannot be undone.`)) handleDeleteFont(font.id);
                    }}
                    style={{
                      fontSize: 12, color: theme.textFaint, cursor: 'pointer',
                    }}
                  >✕</span>
                )}
                <span
                  onClick={e => { e.stopPropagation(); toggleActivation(font.id).then(r => r?.active_count != null && setActiveCount(r.active_count)); }}
                  style={{
                    fontSize: 12, textTransform: 'lowercase', cursor: 'pointer',
                    color: font.is_activated ? theme.accent : theme.text,
                    borderBottom: `1px solid ${font.is_activated ? theme.accent : theme.borderMedium}`,
                  }}
                >{font.is_activated ? 'active' : 'activate'}</span>
              </div>
            </div>
          </div>
          );
        })}

        {!fontsLoading && fonts.length === 0 && (
          <div style={{
            gridColumn: '1 / -1', textAlign: 'center',
            padding: 60, color: theme.textFaint,
          }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.5 }}>◈</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No fonts found</div>
            <div style={{ fontSize: 12 }}>
              {searchQuery ? 'Try a different search term' : 'Upload some fonts to get started'}
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16,
          marginTop: 24, paddingBottom: 16,
        }}>
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            style={{
              background: 'none', border: `1px solid ${theme.border}`,
              borderRadius: 0, color: theme.text, padding: '6px 14px',
              cursor: page <= 1 ? 'not-allowed' : 'pointer', fontSize: 12,
              fontFamily: 'inherit', opacity: page <= 1 ? 0.3 : 1, textTransform: 'lowercase',
            }}
          >&larr; prev</button>
          <span style={{
            fontSize: 12, color: theme.textMuted,
          }}>
            page {page} of {pagination.pages}
          </span>
          <button
            disabled={page >= pagination.pages}
            onClick={() => setPage(p => p + 1)}
            style={{
              background: 'none', border: `1px solid ${theme.border}`,
              borderRadius: 0, color: theme.text, padding: '6px 14px',
              cursor: page >= pagination.pages ? 'not-allowed' : 'pointer', fontSize: 12,
              fontFamily: 'inherit', opacity: page >= pagination.pages ? 0.3 : 1, textTransform: 'lowercase',
            }}
          >next &rarr;</button>
        </div>
      )}
    </div>
  );
}
